import { Fragment, useState } from 'react';

import { Snapshot, useRecoilTransactionObserver_UNSTABLE } from 'recoil';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import useRecoilDebuggerStore from '@/store/RecoilDebugger/useRecoilDebuggerStore';

// Utility function to get all Recoil values
const getAllRecoilValues = (snapshot: Snapshot) => {
  try {
    const nodes = Array.from(snapshot.getNodes_UNSTABLE());
    return nodes.map((node) => {
      const loadable = snapshot.getLoadable(node);
      return {
        key: node.key,
        value:
          loadable.state === 'hasValue' ? loadable.contents : 'Loading/Error',
      };
    });
  } catch (error) {
    console.error('Error accessing Recoil snapshot:', error);
    return [];
  }
};

const RecoilDebugger = () => {
  const { isRecoilDrawerOpen, setIsReoilDrawerOpen } = useRecoilDebuggerStore();

  const [recoilValues, setRecoilValues] = useState<
    { key: string; value: unknown }[]
  >([]);
  const [filterText, setFilterText] = useState('');

  useRecoilTransactionObserver_UNSTABLE(({ snapshot }) => {
    if (isRecoilDrawerOpen) {
      const values = getAllRecoilValues(snapshot);
      setRecoilValues(values);

      console.debug('The following atoms were modified:');
      try {
        for (const node of snapshot.getNodes_UNSTABLE({ isModified: true })) {
          console.debug(node.key, snapshot.getLoadable(node));
        }
      } catch (error) {
        console.error(
          'Error accessing modified nodes in Recoil snapshot:',
          error,
        );
      }
    }
  });

  if (!isRecoilDrawerOpen) return null;

  const filteredRecoilAtoms = recoilValues.filter(({ key }) => {
    const normalizedKey = key.replace(/[-\s]/g, '').toLowerCase();
    const normalizedFilterText = filterText.replace(/[-\s]/g, '').toLowerCase();
    return normalizedKey.includes(normalizedFilterText);
  });

  return (
    <Drawer
      anchor="right"
      open={isRecoilDrawerOpen}
      variant="permanent"
      sx={{ width: 300 }}
    >
      <Box sx={{ width: 300, p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">Recoil State</Typography>
          <IconButton onClick={() => setIsReoilDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />

        <TextField
          placeholder='Filter by key (e.g., "user")'
          fullWidth
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ my: 2 }}
        />
        <List>
          {filteredRecoilAtoms.map(({ key, value }) => (
            <Fragment key={key}>
              <Divider />
              <ListItem key={key} sx={{ display: 'block', px: 0 }}>
                <ListItemText
                  primary={key}
                  secondary={
                    value == null ? `${value}` : JSON.stringify(value, null, 2)
                  }
                  sx={{ wordWrap: 'break-word' }}
                />
              </ListItem>
            </Fragment>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default RecoilDebugger;
